// --- src/components/PosHistory.tsx ---
import React, { useState, useEffect } from 'react';
import {
  Clock,
  ShoppingCart,
  Receipt,
  Filter,
  X,
  RotateCcw,
  Undo2,
  FileText,
  Loader2,
  DollarSign,
  Package,
  Printer,
} from 'lucide-react';
import { supabaseService } from '../services/supabaseService';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'react-hot-toast';
import { generateReceiptPDF } from '../services/pdfService';
import { ConfirmDialog } from './ConfirmDialog';

interface CartTransaction {
  id: string;
  payment_type: string;
  total_amount: number;
  total_items: number;
  created_at: string;
  seller_name?: string;
  pult_name?: string;
  notes?: string;
  discount_percent?: number;
  discount_amount?: number;
  items: Array<{
    ean: string;
    product_name: string;
    quantity: number;
    unit_price: number; // Gross unit price
    total_price: number; // Gross total price
    vat_rate?: number; // VAT rate for this item
    unit?: string; // Valid unit (db/kg/m) for display
  }>;
}

interface PosHistoryProps {
  onClose?: () => void;
}

export const PosHistory: React.FC<PosHistoryProps> = ({ onClose }) => {
  const [transactions, setTransactions] = useState<CartTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [filterPaymentType, setFilterPaymentType] = useState<string>('ALL');
  const [filterSeller, setFilterSeller] = useState<string>('ALL');
  const [dateFilter, setDateFilter] = useState<'ALL' | 'TODAY' | 'WEEK' | 'MONTH'>('ALL');
  const [cancelingTransactionId, setCancelingTransactionId] = useState<string | null>(null);
  const [returningItem, setReturningItem] = useState<{
    ean: string;
    name: string;
    quantity: number;
  } | null>(null);
  const [showDailyClosure, setShowDailyClosure] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState<{ transaction: CartTransaction | null }>({
    transaction: null,
  });

  const extractUserNote = (notes?: string) => {
    const raw = (notes || '').trim();
    if (!raw) return '';

    // Our system builds notes like: "Eladó: X | ... | Note: userText"
    // Show only the actual user-entered part.
    const parts = raw
      .split('|')
      .map((p: string) => p.trim())
      .filter(Boolean);
    for (let i = parts.length - 1; i >= 0; i--) {
      const part = parts[i];
      const match = part.match(/^(?:note|megjegyzés)\s*:\s*(.*)$/i);
      if (match) {
        return (match[1] || '').trim();
      }
    }

    return raw;
  };

  useEffect(() => {
    loadTransactions();
  }, [filterPaymentType, filterSeller, dateFilter]);

  // Subscribe to real-time transaction updates
  useEffect(() => {
    const unsubscribe = supabaseService.subscribeToTransactions(() => {
      loadTransactions();
    });

    return unsubscribe;
  }, []);

  const handleReprint = async (transaction: CartTransaction) => {
    setIsPrinting(true);
    try {
      // Map CartTransaction to ReceiptTransaction format
      const receiptData = {
        items: transaction.items.map((item: any) => ({
          ean: item.ean,
          name: item.product_name,
          quantity: item.quantity,
          price: item.unit_price, // Gross unit price
          total: item.total_price, // Gross total price
          vatRate: item.vat_rate || 27, // VAT rate for this item
        })),
        totalAmount:
          transaction.total_amount ||
          transaction.items.reduce((sum: number, item: any) => sum + item.total_price, 0),
        paymentType: transaction.payment_type as 'CASH' | 'INVOICE' | 'CARD' | 'OTHER',
        transactionId: transaction.id,
        date: new Date(transaction.created_at),
        sellerName: transaction.seller_name,
        discountPercent: transaction.discount_percent || 0,
        discountAmount: transaction.discount_amount || 0,
      };

      await generateReceiptPDF(receiptData);
      toast.success('Nyugta PDF sikeresen generálva');
    } catch (error: unknown) {
      console.error('[Reprint] Error generating PDF:', error);
      toast.error(
        `PDF generálási hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      );
    } finally {
      setIsPrinting(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const data = await supabaseService.getCartTransactionHistory(100);

      // Apply filters
      let filtered = data;

      // Filter by payment type
      if (filterPaymentType !== 'ALL') {
        filtered = filtered.filter((t) => t.payment_type === filterPaymentType);
      }

      // Filter by date
      if (dateFilter !== 'ALL') {
        const now = new Date();
        const filterDate = new Date();

        switch (dateFilter) {
          case 'TODAY':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'WEEK':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'MONTH':
            filterDate.setMonth(now.getMonth() - 1);
            break;
        }

        filtered = filtered.filter((t) => {
          const transactionDate = new Date(t.created_at);
          return transactionDate >= filterDate;
        });
      }

      setTransactions(filtered);
    } catch (error) {
      console.error('Error loading cart transactions:', error);
      toast.error('Hiba történt a tranzakciók betöltésekor');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPaymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CASH: 'Készpénz',
      INVOICE: 'Számla',
      CARD: 'Kártya',
      OTHER: 'Egyéb',
    };
    return labels[type] || type;
  };

  const getPaymentTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      CASH: 'bg-green-600/20 text-green-400',
      INVOICE: 'bg-blue-600/20 text-blue-400',
      CARD: 'bg-purple-600/20 text-purple-400',
      OTHER: 'bg-gray-600/20 text-gray-400',
    };
    return colors[type] || 'bg-gray-600/20 text-gray-400';
  };

  if (selectedTransaction) {
    return (
      <div className="p-6 bg-slate-900 text-white min-h-screen">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Receipt className="w-6 h-6 text-blue-400" />
              <h1 className="text-2xl font-bold">Kosár részletek</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleReprint(selectedTransaction)}
                disabled={isPrinting}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition font-medium"
              >
                {isPrinting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Nyomtatás...</span>
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4" />
                    <span>Nyugta nyomtatása</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setSelectedTransaction(null)}
                className="p-2 hover:bg-slate-800 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Transaction Info */}
          <div className="bg-slate-800 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <p className="text-sm text-slate-400">Dátum</p>
                <p className="font-semibold">{formatDate(selectedTransaction.created_at)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-400">Fizetési mód</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm ${getPaymentTypeColor(selectedTransaction.payment_type)}`}
                >
                  {getPaymentTypeLabel(selectedTransaction.payment_type)}
                </span>
              </div>
              <div>
                <p className="text-sm text-slate-400">
                  {selectedTransaction.discount_percent > 0
                    ? 'Végösszeg (kedvezményes)'
                    : 'Összesen (Bruttó)'}
                </p>
                <p className="font-bold text-lg text-green-400">
                  {formatCurrency(
                    selectedTransaction.total_amount ||
                      selectedTransaction.items.reduce(
                        (sum: number, item: any) => sum + item.total_price,
                        0
                      )
                  )}
                </p>
              </div>
              {selectedTransaction.discount_percent > 0 && (
                <div>
                  <p className="text-sm text-slate-400">Kedvezmény</p>
                  <p className="font-semibold text-orange-400">
                    -{selectedTransaction.discount_percent}% (
                    {formatCurrency(selectedTransaction.discount_amount || 0)})
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-400">Tételek száma</p>
                <p className="font-semibold">
                  {selectedTransaction.total_items ||
                    selectedTransaction.items.reduce(
                      (sum: number, item: any) => sum + item.quantity,
                      0
                    )}{' '}
                  tétel
                </p>
              </div>
              {selectedTransaction.seller_name && (
                <div>
                  <p className="text-sm text-slate-400">Eladó</p>
                  <p className="font-semibold">{selectedTransaction.seller_name}</p>
                </div>
              )}
              {selectedTransaction.pult_name && (
                <div>
                  <p className="text-sm text-slate-400">Pult</p>
                  <p className="font-semibold">{selectedTransaction.pult_name}</p>
                </div>
              )}
            </div>

            {/* Net and VAT Breakdown */}
            {(() => {
              const totalGross =
                selectedTransaction.total_amount ||
                selectedTransaction.items.reduce(
                  (sum: number, item: any) => sum + item.total_price,
                  0
                );
              // Use first item's VAT rate or default to 27%
              // If items have different VAT rates, we use the first one (most common case)
              const vatRate = selectedTransaction.items[0]?.vat_rate || 27;
              const totalNet = Math.round((totalGross / (1 + vatRate / 100)) * 100) / 100;
              const totalVat = totalGross - totalNet;

              return (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-700">
                  <div>
                    <p className="text-sm text-slate-400">Nettó</p>
                    <p className="font-semibold text-green-300">{formatCurrency(totalNet)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">ÁFA ({vatRate}%)</p>
                    <p className="font-semibold text-green-300">{formatCurrency(totalVat)}</p>
                  </div>
                </div>
              );
            })()}

            {selectedTransaction.notes && selectedTransaction.notes.trim().length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-sm text-slate-400 mb-1">Megjegyzés</p>
                <p className="text-white whitespace-pre-wrap break-words">
                  {extractUserNote(selectedTransaction.notes)}
                </p>
              </div>
            )}
          </div>

          {/* Items List */}
          <div className="bg-slate-800 rounded-lg overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Termék</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Mennyiség</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Egységár</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Összesen</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Művelet</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTransaction.items.map((item: any, idx: number) => (
                    <tr key={idx} className="border-t border-slate-700 hover:bg-slate-700/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-slate-400">EAN: {item.ean}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {item.quantity} {item.unit || 'db'}
                      </td>
                      <td className="px-4 py-3 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-4 py-3 text-right font-semibold">
                        {formatCurrency(item.total_price)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={async () => {
                            if (returningItem) return;
                            setReturningItem({
                              ean: item.ean,
                              name: item.product_name,
                              quantity: item.quantity,
                            });
                            try {
                              // Keresünk supplier adatot
                              const {
                                data: { user },
                              } = await supabase.auth.getUser();

                              if (!user) {
                                throw new Error('User not authenticated');
                              }

                              const { data: supplierData } = await supabase
                                .from('supplier_products')
                                .select('name, price, selling_price')
                                .eq('ean', item.ean)
                                .eq('user_id', user.id)
                                .single();

                              const productDetails = supplierData
                                ? {
                                    ean: item.ean,
                                    name: supplierData.name,
                                    sku: '',
                                    netPrice: Number(supplierData.price) || 0,
                                    sellingPrice: Number(supplierData.selling_price) || 0,
                                  }
                                : undefined;

                              await supabaseService.processReturn(
                                item.ean,
                                item.quantity,
                                productDetails
                              );

                              toast.success(
                                `Visszatérítés sikeres: ${item.product_name} (${item.quantity} ${item.unit || 'db'})`
                              );
                              setReturningItem(null);
                              // Frissítjük a tranzakció listát
                              loadTransactions();
                            } catch (error: unknown) {
                              console.error('Error processing return:', error);
                              toast.error(
                                `Visszatérítés hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
                              );
                              setReturningItem(null);
                            }
                          }}
                          disabled={returningItem !== null}
                          className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg text-sm transition flex items-center gap-1"
                        >
                          {returningItem?.ean === item.ean ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Visszatérítés...
                            </>
                          ) : (
                            <>
                              <RotateCcw className="w-4 h-4" />
                              Visszatérítés
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (cancelingTransactionId) return;
                setConfirmCancel({ transaction: selectedTransaction });
              }}
              disabled={cancelingTransactionId !== null}
              className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg font-semibold transition flex items-center justify-center gap-2"
            >
              {cancelingTransactionId === selectedTransaction.id ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Visszavonás...
                </>
              ) : (
                <>
                  <Undo2 className="w-5 h-5" />
                  Teljes tranzakció visszavonása
                </>
              )}
            </button>
          </div>
        </div>

        {/* Confirmation Dialog for Transaction Cancellation */}
        <ConfirmDialog
          isOpen={confirmCancel.transaction !== null}
          title="Tranzakció visszavonása"
          message={
            confirmCancel.transaction
              ? `Biztosan visszavonod ezt a tranzakciót?\n\nDátum: ${formatDate(
                  confirmCancel.transaction.created_at
                )}\nÖsszeg: ${formatCurrency(
                  confirmCancel.transaction.total_amount ||
                    confirmCancel.transaction.items.reduce(
                      (sum: number, item: any) => sum + item.total_price,
                      0
                    )
                )}\n\nA készlet visszaállításra kerül.`
              : ''
          }
          confirmText="Visszavonás"
          cancelText="Mégse"
          variant="danger"
          onConfirm={async () => {
            if (!confirmCancel.transaction) return;
            const transaction = confirmCancel.transaction;
            setConfirmCancel({ transaction: null });
            setCancelingTransactionId(transaction.id);
            try {
              await supabaseService.cancelCartTransaction(
                transaction.id,
                supabaseService.updateItemQuantity.bind(supabaseService)
              );
              toast.success('Tranzakció sikeresen visszavonva és készlet visszaállítva');
              setSelectedTransaction(null);
              setCancelingTransactionId(null);
              loadTransactions();
            } catch (error: unknown) {
              console.error('Error canceling transaction:', error);
              toast.error(
                `Visszavonás hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
              );
              setCancelingTransactionId(null);
            }
          }}
          onCancel={() => {
            setConfirmCancel({ transaction: null });
          }}
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-slate-900 text-white min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-blue-400" />
            <h1 className="text-2xl font-bold">Kosár előzmények</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDailyClosure(true)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold transition flex items-center gap-2"
            >
              <FileText className="w-5 h-5" />
              Napi zárás
            </button>
            {onClose && (
              <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-400">Szűrők:</span>
            </div>

            <select
              value={filterPaymentType}
              onChange={(e) => setFilterPaymentType(e.target.value)}
              className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="ALL">Összes fizetési mód</option>
              <option value="CASH">Készpénz</option>
              <option value="INVOICE">Számla</option>
              <option value="CARD">Kártya</option>
              <option value="OTHER">Egyéb</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as any)}
              className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="ALL">Összes dátum</option>
              <option value="TODAY">Ma</option>
              <option value="WEEK">Utolsó 7 nap</option>
              <option value="MONTH">Utolsó 30 nap</option>
            </select>

            <select
              value={filterSeller}
              onChange={(e) => setFilterSeller(e.target.value)}
              className="bg-slate-700 text-white px-3 py-2 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            >
              <option value="ALL">Összes eladó</option>
              {Array.from(
                new Set(
                  transactions.map((t) => t.seller_name).filter((name): name is string => !!name)
                )
              )
                .sort()
                .map((seller: string) => (
                  <option key={seller} value={seller}>
                    {seller}
                  </option>
                ))}
            </select>
          </div>
        </div>

        {/* Transactions List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p className="mt-4 text-slate-400">Betöltés...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-12 bg-slate-800 rounded-lg">
            <ShoppingCart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Nincsenek tranzakciók</p>
          </div>
        ) : (
          <div className="bg-slate-800 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Dátum</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Fizetési mód</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Eladó</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Pult</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Tételek</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Összesen</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Művelet</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction: any) => (
                    <tr
                      key={transaction.id}
                      className="border-t border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                      onClick={() => setSelectedTransaction(transaction)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          <span>{formatDate(transaction.created_at)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-sm ${getPaymentTypeColor(transaction.payment_type)}`}
                        >
                          {getPaymentTypeLabel(transaction.payment_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300">{transaction.seller_name || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300">{transaction.pult_name || '-'}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {transaction.total_items ||
                          transaction.items.reduce(
                            (sum: number, item: any) => sum + item.quantity,
                            0
                          )}{' '}
                        tétel
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-400">
                        {formatCurrency(
                          transaction.total_amount ||
                            transaction.items.reduce(
                              (sum: number, item: any) => sum + item.total_price,
                              0
                            )
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTransaction(transaction);
                            }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition"
                          >
                            Részletek
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (cancelingTransactionId) return;
                              setConfirmCancel({ transaction });
                            }}
                            disabled={cancelingTransactionId !== null}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-lg text-sm transition flex items-center gap-1"
                            title="Tranzakció visszavonása és készlet visszaállítása"
                          >
                            {cancelingTransactionId === transaction.id ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Visszavonás...
                              </>
                            ) : (
                              <>
                                <Undo2 className="w-4 h-4" />
                                Visszavonás
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Napi zárás modal */}
        {showDailyClosure && (
          <DailyClosureModal
            onClose={() => setShowDailyClosure(false)}
            onRefresh={loadTransactions}
          />
        )}

        {/* Confirmation Dialog for Transaction Cancellation */}
        <ConfirmDialog
          isOpen={confirmCancel.transaction !== null}
          title="Tranzakció visszavonása"
          message={
            confirmCancel.transaction
              ? `Biztosan visszavonod ezt a tranzakciót?\n\nDátum: ${formatDate(
                  confirmCancel.transaction.created_at
                )}\nÖsszeg: ${formatCurrency(
                  confirmCancel.transaction.total_amount ||
                    confirmCancel.transaction.items.reduce(
                      (sum: number, item: any) => sum + item.total_price,
                      0
                    )
                )}\n\nA készlet visszaállításra kerül.`
              : ''
          }
          confirmText="Visszavonás"
          cancelText="Mégse"
          variant="danger"
          onConfirm={async () => {
            if (!confirmCancel.transaction) return;
            const transaction = confirmCancel.transaction;
            if (!transaction) return;
            setConfirmCancel({ transaction: null });
            setCancelingTransactionId(transaction.id);
            try {
              await supabaseService.cancelCartTransaction(transaction.id);
              toast.success('Tranzakció sikeresen visszavonva és készlet visszaállítva');
              if (selectedTransaction && selectedTransaction.id === transaction.id) {
                setSelectedTransaction(null);
              }
              setCancelingTransactionId(null);
              loadTransactions();
            } catch (error: unknown) {
              console.error('Error canceling transaction:', error);
              toast.error(
                `Visszavonás hiba: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
              );
              setCancelingTransactionId(null);
            }
          }}
          onCancel={() => {
            setConfirmCancel({ transaction: null });
          }}
        />
      </div>
    </div>
  );
};

// Napi zárás komponens
interface DailyClosureModalProps {
  onClose: () => void;
  onRefresh: () => void;
}

const DailyClosureModal: React.FC<DailyClosureModalProps> = ({
  onClose,
  onRefresh: _onRefresh,
}) => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [closureData, setClosureData] = useState<{
    date: string;
    totalTransactions: number;
    totalRevenue: number;
    totalItems: number;
    transactionsByPaymentType: Record<string, { count: number; amount: number }>;
    transactions: Array<{
      id: string;
      payment_type: string;
      total_amount: number;
      total_items: number;
      created_at: string;
    }>;
  } | null>(null);

  useEffect(() => {
    loadDailyClosure();
  }, [selectedDate]);

  const loadDailyClosure = async () => {
    setLoading(true);
    try {
      const date = new Date(selectedDate);
      const data = await supabaseService.getDailyClosure(date);
      setClosureData(data);
    } catch (error: unknown) {
      console.error('Error loading daily closure:', error);
      toast.error(
        `Hiba a napi zárás betöltésekor: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getPaymentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CASH: 'Készpénz',
      INVOICE: 'Számla',
      CARD: 'Kártya',
      OTHER: 'Egyéb',
    };
    return labels[type] || type;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold">Napi zárás</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Date Selector */}
        <div className="p-6 border-b border-slate-700">
          <label className="block text-sm font-semibold text-slate-400 mb-2">
            Dátum kiválasztása
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-slate-700 text-white px-4 py-2 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-400" />
              <p className="text-slate-400">Betöltés...</p>
            </div>
          ) : closureData ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Tranzakciók száma</p>
                  <p className="text-2xl font-bold">{closureData.totalTransactions}</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Összes bevétel</p>
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(closureData.totalRevenue)}
                  </p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Összes tétel</p>
                  <p className="text-2xl font-bold">{closureData.totalItems} tétel</p>
                </div>
                <div className="bg-slate-700 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-1">Átlagos tranzakció</p>
                  <p className="text-2xl font-bold">
                    {closureData.totalTransactions > 0
                      ? formatCurrency(closureData.totalRevenue / closureData.totalTransactions)
                      : '0 Ft'}
                  </p>
                </div>
              </div>

              {/* Payment Type Breakdown */}
              <div className="bg-slate-700 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                  Fizetési módok szerinti bontás
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(closureData.transactionsByPaymentType).map(
                    ([type, data]: [string, any]) => (
                      <div key={type} className="bg-slate-800 rounded-lg p-4">
                        <p className="text-sm text-slate-400 mb-1">{getPaymentTypeLabel(type)}</p>
                        <p className="text-xl font-bold">{data.count} tétel</p>
                        <p className="text-sm text-green-400">{formatCurrency(data.amount)}</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Transactions List */}
              <div className="bg-slate-700 rounded-lg overflow-hidden">
                <h3 className="text-lg font-semibold p-4 border-b border-slate-600 flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" />
                  Tranzakciók listája
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Időpont</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold">Fizetési mód</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Tételek</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold">Összeg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closureData.transactions.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                            Nincsenek tranzakciók ezen a napon
                          </td>
                        </tr>
                      ) : (
                        closureData.transactions.map((transaction: any) => (
                          <tr
                            key={transaction.id}
                            className="border-t border-slate-600 hover:bg-slate-800/50"
                          >
                            <td className="px-4 py-3">{formatDate(transaction.created_at)}</td>
                            <td className="px-4 py-3">
                              {getPaymentTypeLabel(transaction.payment_type)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {transaction.total_items} tétel
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-green-400">
                              {formatCurrency(transaction.total_amount)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-400">Nincs adat</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
